'use client';

import React, { useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface ServiceDetail {
  id: number;
  title: string;
  description: string;
  fullDescription: string;
  price: string;
  image: string;
  alt: string;
  category: 'health' | 'community';
  location: string[];
  features: string[];
  eligibility: string[];
  successStory: {
    name: string;
    story: string;
    image: string;
    alt: string;
  };
}

interface ServiceDetailModalProps {
  service: ServiceDetail | null;
  isOpen: boolean;
  onClose: () => void;
  onContact: () => void;
  onWhatsApp: () => void;
}

const ServiceDetailModal: React.FC<ServiceDetailModalProps> = ({
  service,
  isOpen,
  onClose,
  onContact,
  onWhatsApp,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !service) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card rounded-xl shadow-elevated max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between z-10">
          <h2 className="text-2xl font-heading font-bold text-foreground">{service.title}</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors duration-200"
            aria-label="Close modal"
          >
            <Icon name="XMarkIcon" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Image */}
          <div className="relative h-64 rounded-xl overflow-hidden">
            <AppImage
              src={service.image}
              alt={service.alt}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Price and Locations */}
          <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-muted rounded-lg">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Relief Rate</p>
              <p className="text-3xl font-heading font-bold text-primary">{service.price}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {service.location.map((loc, index) => (
                <span
                  key={index}
                  className="px-3 py-1 rounded-full bg-secondary/10 text-secondary text-sm font-medium flex items-center space-x-1"
                >
                  <Icon name="MapPinIcon" size={14} />
                  <span>{loc}</span>
                </span>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <h3 className="text-lg font-heading font-bold text-foreground mb-3">About This Service</h3>
            <p className="text-foreground leading-relaxed">{service.fullDescription}</p>
          </div>

          {/* Features */}
          <div>
            <h3 className="text-lg font-heading font-bold text-foreground mb-3">What's Included</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {service.features.map((feature, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <Icon name="CheckCircleIcon" size={20} className="text-secondary mt-0.5 flex-shrink-0" />
                  <span className="text-foreground">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Eligibility */}
          <div>
            <h3 className="text-lg font-heading font-bold text-foreground mb-3">Eligibility Criteria</h3>
            <div className="space-y-2">
              {service.eligibility.map((criteria, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <Icon name="InformationCircleIcon" size={20} className="text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-foreground">{criteria}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Success Story */}
          <div className="bg-muted rounded-lg p-6">
            <h3 className="text-lg font-heading font-bold text-foreground mb-4">Success Story</h3>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0">
                <AppImage
                  src={service.successStory.image}
                  alt={service.successStory.alt}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <p className="font-heading font-semibold text-foreground mb-2">{service.successStory.name}</p>
                <p className="text-muted-foreground italic">&quot;{service.successStory.story}&quot;</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <button
              onClick={onContact}
              className="flex-1 px-6 py-3 rounded-lg font-heading font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 flex items-center justify-center space-x-2"
            >
              <Icon name="PhoneIcon" size={20} />
              <span>Call Now</span>
            </button>
            <button
              onClick={onWhatsApp}
              className="flex-1 px-6 py-3 rounded-lg font-heading font-semibold bg-secondary text-secondary-foreground hover:bg-secondary/90 transition-all duration-300 flex items-center justify-center space-x-2"
            >
              <Icon name="ChatBubbleLeftRightIcon" size={20} />
              <span>WhatsApp</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetailModal;